import { useState } from 'react';
import { useAppDispatch } from '@store/hooks';
import { createMultipleContents } from '@store/features/content/contentSlice';
import { Content } from '@services/content-service';
import { ApiError } from '@services/api/types';

interface UseCreateContentResult {
  createContent: (contents: Omit<Content, 'status'>[]) => Promise<void>;
  isCreating: boolean;
  error: string | null;
  errorDetails: string | null;
}

/**
 * Hook personalizado para gestionar la creación de contenido
 * @returns Objeto con la función de creación, estado de carga y errores
 */
export const useCreateContent = (): UseCreateContentResult => {
  const dispatch = useAppDispatch();
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);

  const createContent = async (contents: Omit<Content, 'status'>[]) => {
    setIsCreating(true);
    setError(null);
    setErrorDetails(null);

    try {
      await dispatch(createMultipleContents(contents)).unwrap();
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage =
        typeof apiError.error?.message === 'string'
          ? apiError.error.message
          : 'Error creating content';

      const errorDetailsMessage =
        typeof apiError.error?.details === 'string'
          ? apiError.error.details
          : typeof apiError.error?.details?.specificDetails === 'string'
            ? apiError.error.details.specificDetails
            : 'An unexpected error occurred';

      setError(errorMessage);
      setErrorDetails(errorDetailsMessage);
      throw error; // Re-throw para manejo en el componente si es necesario
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createContent,
    isCreating,
    error,
    errorDetails,
  };
};
